import { useEffect, useRef, useState } from 'react';
import { BraintreeError, Client, HostedFields, hostedFields } from 'braintree-web';
import { Button, ButtonProps, CircularProgress, Icon, styled, Typography, useTheme } from '@mui/material';
import { Alert } from '@/components/DynamicUI/Alert';
import { AlertSeverity } from '@/constants/alertTypes';
import { ControllerRenderProps } from 'react-hook-form';
import useAnalytics from '@/hooks/use-analytics';
import { reportError } from '@/services/error-reporting-service';
import { InertIcon } from '../InertIcon';
import { IconColors, IconSizes } from '@/constants/iconTypes';

interface BraintreeCreditCardProps {
  onSuccess: (nonce: string) => Promise<void>;
  clientInstance: Client;
  legal: string;
  amount: string;
  fieldProps: ControllerRenderProps;
  serverError: CreditCardError | null;
}

interface BraintreeErrorDetails {
  invalidFieldKeys: string[];
  invalidFields: Record<string, HTMLDivElement>;
}

interface ExtendedBraintreeError extends BraintreeError {
  details: BraintreeErrorDetails;
}

export interface CreditCardError {
  message: string;
  details: BraintreeErrorDetails | null;
}

// Braintree nests the underlying network-level error under details.originalError.
// Extract only its scalar fields for logging (never the whole object — circular
// refs) so the real cause of Hosted Fields setup/tokenize failures reaches Sentry
// instead of the generic "timed out" message. (Per Braintree's investigation ask.)
function braintreeOriginalErrorContext(error: unknown): Record<string, unknown> | undefined {
  if (!error || typeof error !== 'object' || !('details' in error)) return undefined;
  const details = error.details;
  if (!details || typeof details !== 'object' || !('originalError' in details)) return undefined;
  const originalError = details.originalError;
  if (!originalError || typeof originalError !== 'object') return undefined;
  const oe = originalError as Record<string, unknown>;
  return {
    originalErrorMessage: oe.message,
    originalErrorCode: oe.code,
    originalErrorName: oe.name,
    originalErrorType: oe.type,
  };
}

export function BraintreeCreditCardForm({
  onSuccess,
  clientInstance,
  amount,
  legal,
  serverError,
}: BraintreeCreditCardProps) {
  const creditCards = import.meta.glob('/src/assets/logos/creditCards/*.svg', {
    eager: true,
    query: '?url',
    import: 'default',
  });

  const hostedFieldsInstance = useRef<HostedFields | null>(null);
  const hasSetupInitialized = useRef(false);

  const [showLoadingState, setShowLoadingState] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [braintreeError, setBraintreeError] = useState<CreditCardError | null>(null);
  // Local errors come from hosted-field validation; server errors come from bind/payment failure.
  const displayError = braintreeError ?? serverError;

  const lastFocusedFields = useRef<Record<string, number>>({});
  const { trackEvent } = useAnalytics();
  const theme = useTheme();

  // Initialize Braintree Hosted Fields
  useEffect(() => {
    async function setupBraintree() {
      try {
        const fields = await hostedFields.create({
          client: clientInstance,
          styles: {
            input: {
              color: theme.palette.text.primary,
              'font-family': '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;',
              'font-size': '16px',
            },
            '::placeholder': {
              color: theme.palette.text.secondary,
            },
          },
          fields: {
            cardholderName: {
              selector: '#card-holder-name',
              placeholder: 'Cardholder name',
            },
            number: {
              selector: '#card-number',
              placeholder: 'Card number',
              maxCardLength: 16,
            },
            cvv: {
              selector: '#cvv',
              placeholder: 'CVV',
            },
            expirationDate: {
              selector: '#expiration-date',
              placeholder: 'Expiration date',
            },
            postalCode: {
              selector: '#postal-code',
              placeholder: 'ZIP code',
            },
          },
        });

        hostedFieldsInstance.current = fields;

        // Set up event listeners immediately after fields are created
        const onValidityChange = (event: { emittedBy: string; fields: Record<string, { isValid: boolean }> }) => {
          setBraintreeError((currentErrors) => {
            if (!currentErrors?.details) {
              return currentErrors;
            }

            const field = event.emittedBy;
            const isValid = event.fields[field]?.isValid;

            if (!currentErrors.details.invalidFieldKeys.includes(field) || !isValid) {
              return currentErrors;
            }

            const invalidFieldKeys = currentErrors.details.invalidFieldKeys.filter((key) => key !== field);

            if (invalidFieldKeys.length === 0) {
              return null;
            }

            return {
              message: currentErrors.message,
              details: {
                invalidFieldKeys,
                invalidFields: Object.fromEntries(
                  Object.entries(currentErrors.details.invalidFields).filter(([key]) => key !== field)
                ),
              },
            };
          });
        };

        const onFocus = (event: { emittedBy: string }) => {
          const field = event.emittedBy;
          const lastFocusedAt = lastFocusedFields.current[field] ?? 0;
          const now = Date.now();

          if (now - lastFocusedAt < 1000) {
            return;
          }

          lastFocusedFields.current[field] = now;

          trackEvent('BRAINTREE_HOSTED_FIELD_FOCUSED', {
            braintree_field: field,
          });
        };

        fields.on('validityChange', onValidityChange);
        fields.on('focus', onFocus);

        setShowLoadingState(false);
      } catch (error) {
        reportError({ error, context: braintreeOriginalErrorContext(error) });
      }
    }

    if (hasSetupInitialized.current) return;
    hasSetupInitialized.current = true;
    void setupBraintree();
  }, [clientInstance, theme, trackEvent]);

  // Handle form submission and tokenize the card details
  async function addCard(): Promise<void> {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (braintreeError) {
        setBraintreeError(null);
      }
      const payload = await hostedFieldsInstance.current?.tokenize();
      if (payload) {
        trackEvent('BRAINTREE_CREDIT_CARD_PAYMENT_AUTHORIZED');
        await onSuccess(payload.nonce);
      }
    } catch (error: unknown) {
      trackEvent('BRAINTREE_ADDING_CREDIT_CARD_FAILED');
      if (error && typeof error === 'object' && 'name' in error && error.name === 'BraintreeError') {
        if ((error as ExtendedBraintreeError).code) {
          const braintreeErr = error as ExtendedBraintreeError;
          switch (braintreeErr.code) {
            case 'HOSTED_FIELDS_FIELDS_EMPTY':
              setBraintreeError({
                message: 'All fields are empty! Please fill out the form.',
                details: braintreeErr.details,
              });
              break;
            case 'HOSTED_FIELDS_FIELDS_INVALID':
              setBraintreeError({
                message: 'Please check your payment details and try again.',
                details: braintreeErr.details,
              });
              break;
            case 'HOSTED_FIELDS_FAILED_TOKENIZATION':
              setBraintreeError({
                message: 'Tokenization failed server side. Is the card valid?',
                details: null,
              });
              break;
            case 'HOSTED_FIELDS_TOKENIZATION_NETWORK_ERROR':
              console.error('Network error occurred when tokenizing.');
              setBraintreeError({
                message: 'Network error occurred. Please try again.',
                details: null,
              });
              break;
            default:
              setBraintreeError({
                message: 'Payment failed. Please try again.',
                details: null,
              });
          }
        }
      } else {
        reportError({ error, context: braintreeOriginalErrorContext(error) });
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <PaymentHeader>
        <Typography
          color={theme.palette.text.secondary}
          fontFamily={theme.typography.fontFamily}
          fontSize={theme.typography.body1.fontSize}
          fontWeight={500}
        >
          Credit/Debit
        </Typography>
        <CreditCards>
          {Object.entries(creditCards)
            .filter(([path]) => !path.includes('FallbackCard'))
            .map(([path, url]) => (
              <img
                key={path}
                src={url as string}
                alt={path
                  .split('/')
                  .pop()
                  ?.replace(/\.svg$/, '')}
              />
            ))}
        </CreditCards>
      </PaymentHeader>
      {displayError?.message && (
        <Alert
          severity={AlertSeverity.ERROR}
          titleGutterBottom={false}
          title='Payment Error'
          text={displayError.message}
        />
      )}
      {showLoadingState && (
        <LoaderContainer>
          <CircularProgress color='primary' />
        </LoaderContainer>
      )}
      <StyledContainer isLoading={showLoadingState}>
        <HostedField
          id='card-holder-name'
          name='cardholderName'
          error={displayError}
        />
        <HostedField
          id='card-number'
          name='number'
          error={displayError}
        />
        <HostedField
          id='expiration-date'
          name='expirationDate'
          error={displayError}
        />
        <HostedField
          id='cvv'
          name='cvv'
          error={displayError}
        >
          <StyledIcon>
            <InertIcon
              icon_name='payment'
              size={IconSizes.SMALL}
              color={IconColors.TEXT}
            />
          </StyledIcon>
        </HostedField>
        <HostedField
          id='postal-code'
          name='postalCode'
          error={displayError}
        />
        <Typography
          color={theme.palette.text.secondary}
          fontSize={theme.typography.body2.fontSize}
          style={{ gridColumn: '1 / -1', marginBottom: '0.5rem' }}
        >
          {legal}
        </Typography>
        <StyledButton
          onClick={() => {
            void addCard();
          }}
          disabled={isSubmitting}
          loading={isSubmitting}
          fullWidth={true}
          variant={'contained'}
          role='submit'
          size={'large'}
          startIcon={<Icon>credit_card</Icon>}
          color={'primary'}
        >
          Submit payment of ${amount}
        </StyledButton>
      </StyledContainer>
    </>
  );
}

interface StyledFormProps {
  isLoading: boolean;
}

interface HostedFieldProps {
  name: string;
  error: CreditCardError | null;
}

const StyledContainer = styled('div')<StyledFormProps>`
  visibility: ${({ isLoading }) => (isLoading ? 'hidden' : 'visible')};
  height: ${({ isLoading }) => (isLoading ? '0' : 'auto')};
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-top: 16px;

  & > :nth-of-type(1),
  & > :nth-of-type(2),
  & > :nth-of-type(5),
  & > :nth-of-type(6) {
    grid-column: 1 / -1;
  }
`;

const HostedField = styled('div')<HostedFieldProps>`
  position: relative;
  outline-style: solid;
  outline-color: ${({ error, name, theme }) =>
    error?.details?.invalidFieldKeys?.includes(name) ? theme.palette.error.main : theme.palette.border.main};
  outline-width: ${({ error, name }) => (error?.details?.invalidFieldKeys?.includes(name) ? '2px' : '1px')};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  height: 3.3125rem;
  padding: 0 14px;
  margin-bottom: ${({ error, name }) => (error?.details?.invalidFieldKeys?.includes(name) ? '1.3125rem' : undefined)};

  ${({ error, name, theme }) =>
    !error?.details?.invalidFieldKeys?.includes(name) &&
    `
      &:hover {
        outline-color: ${theme.palette.text.primary};
      }

    `}

  &.braintree-hosted-fields-focused {
    outline-color: ${({ theme }) => theme.palette.tertiary.main};
    outline-width: 2px;
  }

  &::before {
    content: ${({ error, name }) => (error?.details?.invalidFieldKeys?.includes(name) ? '"Invalid Format"' : '')};
    font-size: 16px;
    color: ${({ theme }) => theme.palette.error.main};
    position: absolute;
    bottom: -1.5rem;
    left: 0.75rem;
  }
`;

const LoaderContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 56px; // Height to match the form when loading
`;

const StyledButton = styled(Button)<ButtonProps>`
  background-color: ${({ theme }) => theme.palette.primary.main};
  & .MuiTouchRipple-root {
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

const PaymentHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0 4px;
`;

const CreditCards = styled('div')`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const StyledIcon = styled('div')`
  display: flex;
  align-items: center;
  position: absolute;
  height: 100%;
  right: 14px;
  top: 0;
  bottom: 0;
`;
